import { useEffect, useRef } from 'react'

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string
      remove: (id: string) => void
      reset: (id?: string) => void
    }
    onTurnstileReady?: () => void
  }
}

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

/**
 * Cloudflare Turnstile on the intake form (08 §1) — the only unauthenticated write in the
 * product.
 *
 * Rendered explicitly rather than by auto-scan so the widget's lifetime matches the component's
 * and a React remount cannot leave two widgets on the page. If no site key is configured the
 * component renders nothing and the form still submits; the server decides whether that is
 * acceptable (it is not, in production — `turnstile.require` fails closed).
 */
export function Turnstile({ onToken }: { onToken: (token: string | null) => void }) {
  const container = useRef<HTMLDivElement>(null)
  const widgetId = useRef<string | null>(null)

  useEffect(() => {
    if (!SITE_KEY || !container.current) return
    let cancelled = false

    function render() {
      if (cancelled || !container.current || !window.turnstile) return
      widgetId.current = window.turnstile.render(container.current, {
        sitekey: SITE_KEY,
        callback: (token: string) => onToken(token),
        'error-callback': () => onToken(null),
        'expired-callback': () => onToken(null),
        theme: 'light',
        action: 'estimate-intake',
      })
    }

    if (window.turnstile) {
      render()
    } else if (!document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
      const script = document.createElement('script')
      script.src = SCRIPT_SRC
      script.async = true
      script.defer = true
      script.onload = render
      document.head.appendChild(script)
    } else {
      const timer = window.setInterval(() => {
        if (window.turnstile) {
          window.clearInterval(timer)
          render()
        }
      }, 100)
      return () => window.clearInterval(timer)
    }

    return () => {
      cancelled = true
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current)
      widgetId.current = null
    }
  }, [onToken])

  if (!SITE_KEY) return null
  return <div ref={container} className="my-2" />
}
